/* eslint-disable prettier/prettier */
'use client';

import { useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import Image from 'next/image';

import secureIcon from '@/assets/icons/cart-page-shield-tick.svg';
import infoRedIcon from '@/assets/icons/info-red-icon.png';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import ButtonLink from '@/components/V2/ButtonLink';
import Paragraph from '@/components/V2/Typography/Paragraph';
import { CustomerTypes } from '@/helpers/constants/shopping';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { findEnrollmentPackItemsInCart } from '@/helpers/utils/enrollmentFlowTracker';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';

import { useStepper } from '../../Stepper';

import '@/styles/globals.v2.css';

import { useRouter, useSearchParams } from 'next/navigation';

import useWindow from '@/helpers/hooks/useWindow';

import Button from '../../Button';

const SESSION_STORAGE_ERROR_MESSAGE = 'Error clearing sessionStorage:';
const ENROLLMENT_ENROLLEE_INFO_ROUTE = '/enrollment/enrolleeinfo';

const OrderSummary = () => {
  const { setCurrentActiveStep, totalSteps } = useStepper(0, 3);
  const {
    totalItemsInCart,
    totalPrice,
    totalPawPoints,
    totalSampleCredits,
    hasItemWithSampleCredits,
    hasItemWithPawPoints,
    qualifiedItemsCount,
    cart,
    isEnrollmentFlow,
    setShowChooseYourCheckoutFlowModal,
  } = useShoppingCartContext();

  const { customer } = useAuthContext();
  const { isBackOffice } = useAppContext();
  const { webalias } = useWebAlias();
  const _window = useWindow();
  const router = useRouter();
  const [checkoutClicked, setCheckoutClicked] = useState(false);
  const searchParams = useSearchParams();
  const isFromPetRecommendations = searchParams.get('isFromPetRecommendations') === 'true';

  // Check if user is already a petPro (customerTypeID === 3) - don't show modal for petPros
  const isPetPro = customer?.customerTypeID === CustomerTypes.PET_PRO;

  const _cart = cart || [];
  const totalActiveSteps = useMemo(() => {
    const ezShipItems = +(qualifiedItemsCount ?? 0);

    if (ezShipItems >= totalSteps) return totalSteps;

    return ezShipItems;
  }, [qualifiedItemsCount, totalSteps]);

  const showPawPoints = useMemo(() => {
    const pawPointsInNumber = totalPawPoints ? +totalPawPoints : 0;
    const isPawClubMember = customer?.customerTypeID === CustomerTypes.PAW_CLUB;

    return (
      hasItemWithPawPoints && pawPointsInNumber > 0 && !isEmpty(customer) && (!isBackOffice ? isPawClubMember : true)
    );
  }, [hasItemWithPawPoints, totalPawPoints, customer, isBackOffice]);

  const showSampleCredits = useMemo(() => {
    const sampleCreditsInNumber = totalSampleCredits ? +totalSampleCredits : 0;

    return hasItemWithSampleCredits && sampleCreditsInNumber > 0 && !isEmpty(customer);
  }, [hasItemWithSampleCredits, totalSampleCredits, customer]);

  const subTotal = useMemo(() => {
    let price = <span>${totalPrice ?? 0}</span>;

    if (showPawPoints && !showSampleCredits) {
      price = (
        <span>
          ${totalPrice ?? 0} (${totalPawPoints} PP)
        </span>
      );
    } else if (!showPawPoints && showSampleCredits) {
      price = (
        <span>
          ${totalPrice ?? 0} (${totalSampleCredits} SC)
        </span>
      );
    } else if (showPawPoints && showSampleCredits) {
      price = (
        <span>
          ${totalPrice ?? 0} (${totalPawPoints} PP) <br /> (${totalSampleCredits} SC)
        </span>
      );
    }
    return price;
  }, [showPawPoints, showSampleCredits, totalPawPoints, totalSampleCredits, totalPrice]);

  useEffect(() => {
    if (cart) {
      try {
        if (cart && globalThis?.window?.dataLayer) {
          globalThis?.window?.dataLayer.push({ ecommerce: null });
          globalThis?.window?.dataLayer.push({
            event: 'view_cart',
            ecommerce: {
              currency: 'USD',
              value: totalPrice ?? 0,
              items:
                cart && cart.length > 0
                  ? cart.map((item, index) => {
                      return {
                        item_id: item.id,
                        item_name: item.name,
                        index: index,
                        item_category: item.category,
                        price: item.price,
                        quantity: item.quantity,
                      };
                    })
                  : [],
            },
          });
        }
      } catch {
        console.log('Failed to log view_cart to gtm');
      }
    }
  }, [cart, totalPrice]);

  const isGuestUserFlow = useMemo(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('isGuestUser') === 'true';
    }
    return false;
  }, []);

  const totalItems = totalItemsInCart ?? 0;

  const hasEnrollmentPackItems = useMemo(() => {
    if (!cart || cart.length === 0) return false;
    const packItems = findEnrollmentPackItemsInCart(cart);
    return packItems.length > 0;
  }, [cart]);

  const proceedToCheckoutUrl = useMemo(() => {
    if (totalItems === 0) return '/products';
    if (hasEnrollmentPackItems) return ENROLLMENT_ENROLLEE_INFO_ROUTE;
    const baseUrl = '/checkout/preorder?v=' + Date.now();
    return isGuestUserFlow ? `${baseUrl}&isGuestUser=true` : baseUrl;
  }, [totalItems, hasEnrollmentPackItems, isGuestUserFlow]);

  useEffect(() => {
    setCurrentActiveStep(totalActiveSteps);
  }, [totalActiveSteps, setCurrentActiveStep]);

  const handleCheckoutClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setCheckoutClicked(true);

    // If cart is empty, redirect to products page
    if (totalItems === 0) {
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem('hasVisitedRecommendationFlow');
        } catch (error) {
          console.warn(SESSION_STORAGE_ERROR_MESSAGE, error);
        }
      }
      router.push(generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias));
      return;
    }

    // Use the computed URL (already handles enrollment pack items)
    router.push(generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias));
  };

  return (
    <>
      <div className="v2-pb-0 md:v2-px-3 w-full">
        <div className="v2-bg-[#F9FAFB] v2-p-4 v2-rounded-xs">
          <h3 className="v2-font-sweet-sans-pro v2-text-2xl v2-text-[#154734] v2-leading-7 v2-mb-4">Order Summary</h3>
          <hr />

          <div className="v2-flex v2-flex-wrap v2-items-center v2-justify-between v2-pt-4">
            <p className="v2-font-sweet-sans-pro v2-text-base v2-text-[#344054] v2-leading-6">
              Item Subtotal{' '}
              <span className="v2-text-[#154734] v2-text-base v2-font-sweet-sans-pro">({totalItemsInCart || 0})</span>
            </p>
            <Paragraph className="v2-ml-auto v2-font-sweet-sans-pro v2-text-[#154734] v2-text-base">
              {subTotal}
            </Paragraph>
          </div>
        </div>

        <div className="v2-mb-4 v2-flex md:v2-flex sm:v2-hidden xs:v2-hidden v2-flex-col v2-items-center v2-gap-3 v2-mt-6">
          {/* Show Button when from pet recommendations AND not already a petPro */}
          {/* petPros (customerTypeID === 3) should go directly to checkout without seeing the modal */}
          {isFromPetRecommendations && !isPetPro ? (
            <Button
              type="button"
              btnSize="lg"
              scheme="primary"
              variant="filled"
              className="v2-text-center v2-py-3 v2-pb-[13px] v2-text-lg v2-w-full v2-font-sweet-sans-pro-semi-bold v2-rounded-xs "
              // id="proceed-to-checkout"
              // eslint-disable-next-line sonarjs/cognitive-complexity
              onClick={() => {
                // If cart is empty, redirect to products page and clear recommendation flow flag
                if (totalItems === 0) {
                  if (typeof window !== 'undefined') {
                    try {
                      sessionStorage.removeItem('hasVisitedRecommendationFlow');
                    } catch (error) {
                      console.warn(SESSION_STORAGE_ERROR_MESSAGE, error);
                    }
                  }
                  router.push(generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias));
                  return;
                }

                // Show modal when coming from pet recommendations flow (and not already a petPro)
                setShowChooseYourCheckoutFlowModal(true);
              }}
            >
              Checkout
            </Button>
          ) : (
            <ButtonLink
              as="a"
              href={generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias)}
              scheme="primary"
              variant="filled"
              className="v2-text-center v2-py-3 v2-pb-[13px] v2-text-lg v2-w-full v2-font-sweet-sans-pro-semi-bold v2-rounded-xs "
              id="proceed-to-checkout"
              onClick={handleCheckoutClick}
            >
              Checkout
            </ButtonLink>
          )}
          {/* {checkoutClicked && (
          <KlaviyoComponent
            type={'events'}
            event={'Started Checkout'}
            data={mapDataToStartedCheckout(_cart, _window?.location.origin)}
          />
        )} */}
          {!isEnrollmentFlow && (
            <ButtonLink
              href={generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias)}
              scheme="primary"
              variant="outlined"
              className="v2-hidden v2-text-center v2-py-3 v2-pb-[13px] v2-text-lg v2-font-sweet-sans-pro-semi-bold v2-rounded-xs v2-border-[#d2d2d2] v2-text-grayscale-95 md:v2-block"
            >
              Continue Shopping
            </ButtonLink>
          )}
        </div>
        <div className="v2-mb-[0px] v2-mt-3 v2-flex v2-items-start v2-gap-3 md:v2-mx-auto md:v2-justify-center">
          <Image src={secureIcon} alt="Info Image" className="v2-h-[30px] v2-w-[30px]" />
          <p className="v2-text-base v2-font-sweet-sans-pro v2-text-[#154734] v2-leading-6">
            Shopping with pawTree.com is safe and secure!
          </p>
        </div>
        <div className="v2-flex v2-items-start v2-gap-3 md:v2-mx-auto md:v2-justify-center v2-hidden">
          <Image src={infoRedIcon} alt="Info Image" className="v2-mr-3 v2-h-[22px] v2-w-[22px] v2-mt-1" />
          <p className="v2-text-base v2-font-sweet-sans-pro-light v2-leading-[26px] v2-text-[#F45A40]">
            Please note: If your cart contains dog or cat food, select Choose Label to include a customized label on
            your bag. If selection is not made you will receive a generic label on the bag. You must choose a label for
            each food item in the cart.
          </p>
        </div>
      </div>
    </>
  );
};

export default OrderSummary;

export const CheckoutBtn = ({ isBackOffice, webalias }: { isBackOffice: Boolean; webalias: string }) => {
  const { totalItemsInCart, setShowChooseYourCheckoutFlowModal, cart } = useShoppingCartContext();
  const { customer } = useAuthContext();
  const totalItems = totalItemsInCart ?? 0;
  const router = useRouter();

  const searchParams = useSearchParams();
  const isFromPetRecommendations = searchParams.get('isFromPetRecommendations') === 'true';

  // Check if user is already a petPro (customerTypeID === 3) - don't show modal for petPros
  const isPetPro = customer?.customerTypeID === CustomerTypes.PET_PRO;

  const isGuestUserFlow = useMemo(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('isGuestUser') === 'true';
    }
    return false;
  }, []);

  const hasEnrollmentPackItems = useMemo(() => {
    if (!cart || cart.length === 0) return false;
    const packItems = findEnrollmentPackItemsInCart(cart);
    return packItems.length > 0;
  }, [cart]);

  const proceedToCheckoutUrl = useMemo(() => {
    if (totalItems === 0) return '/products';
    if (hasEnrollmentPackItems) return ENROLLMENT_ENROLLEE_INFO_ROUTE;
    const baseUrl = '/checkout/preorder?v=' + Date.now();
    return isGuestUserFlow ? `${baseUrl}&isGuestUser=true` : baseUrl;
  }, [totalItems, hasEnrollmentPackItems, isGuestUserFlow]);

  const handleCheckoutClick = (event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
    }

    // If cart is empty, redirect to products page and clear recommendation flow flag
    if (totalItems === 0) {
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem('hasVisitedRecommendationFlow');
        } catch (error) {
          console.warn(SESSION_STORAGE_ERROR_MESSAGE, error);
        }
      }
      router.push(generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias));
      return;
    }

    // If coming from pet recommendations AND user is not already a petPro, show modal
    // petPros (customerTypeID === 3) should go directly to checkout without seeing the modal
    if (isFromPetRecommendations && !isPetPro) {
      setShowChooseYourCheckoutFlowModal(true);
      return;
    }

    // If cart has enrollment pack items (not from pet recommendations), redirect to enrollment flow
    if (hasEnrollmentPackItems) {
      router.push(generateApproperiateURL(ENROLLMENT_ENROLLEE_INFO_ROUTE, isBackOffice ? 'shopping' : webalias));
      return;
    }

    // Normal checkout flow
    localStorage.removeItem('petProfile');
    router.push(generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias));
  };

  // Show Button (not ButtonLink) when from pet recommendations AND not already a petPro
  // This prevents the loading bar from appearing when showing the modal
  if (isFromPetRecommendations && !isPetPro)
    return (
      <Button
        type="button"
        btnSize="lg"
        scheme="primary"
        variant="filled"
        className="v2-text-center v2-text-lg v2-py-2 v2-leading-[30px] v2-font-sweet-sans-pro v2-w-full"
        // id="proceed-to-checkout"
        onClick={handleCheckoutClick}
      >
        Checkout
      </Button>
    );

  return (
    <ButtonLink
      // as="a"
      href={generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias)}
      scheme="primary"
      variant="filled"
      className="v2-text-center v2-text-lg v2-py-2 v2-leading-[30px] v2-font-sweet-sans-pro"
      id="proceed-to-checkout"
      onClick={handleCheckoutClick}
    >
      Checkout
    </ButtonLink>
  );
};
